import React, { ReactNode, useEffect, useMemo } from 'react';
import cn from 'classnames';
import { ReconnectContactAvatarList } from '@components/ContactAvatar';
import styles from './ActivityListItem.module.scss';
import Contact from '@models/Contact';
import { useAuthContext } from '@hooks/use-auth-context';
import { ActivityAnnouncementSubtype, ActivityItem, ActivityItemMetadata } from '@models/Activity';
import ContactLinkComponent from '@components/ContactLink';
import Occasion, { timelineDateDisplay } from '@support/Occasion';
import {
  BadgedAvatar,
  BadgedAvatarGroup,
  ClayBadge,
  ImportAvatars,
} from './ActivityListItemAvatars/ActivityListItemAvatars';
import { Actions } from './ItemActions/ItemActions';
import renderPostMarkdown from './Announcements/renderMarkdown';
import ConnectIntegrations from './Announcements/ConnectIntegrations';
import SearchSuggestions from './Announcements/SearchSuggestions';
import { Link } from 'react-router-dom';
import Group from '@models/Group';
import { ID } from '@app/types';
import useEnabledNotifications from '@views/Settings/panels/Notifications/use-enabled-notifications';
import { useDispatch } from 'react-redux';
import { dismissActivityItem } from '@actions/activity';
import { pluralize } from '@support/string';
import { openActivityItemInModal } from '@actions/ui';
import { SmallButton } from '@components/Button';
import { renderDiffMarkdown } from '@support/markdown';

interface ActivityListItemComponentProps {
  item: ActivityItem;
  date: Occasion;
  selected: boolean;
  noContactActions?: boolean;
  noDismissal?: boolean;
  onlyRelativeDate?: boolean;
}

const ContactLink = ({ contact, children }: { contact: Contact; children: ReactNode }) => {
  return (
    <ContactLinkComponent contact={contact} className={styles.contactLink}>
      {children}
    </ContactLinkComponent>
  );
};

const DateDisplay = ({
  date,
  className,
  selected,
}: {
  date: Occasion;
  className?: string;
  selected?: boolean;
}) => (
  <span className={cn(styles.relativeDate, className, selected && styles.hidden)}>
    {timelineDateDisplay(date)}
  </span>
);

const BirthdayActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
}: ActivityListItemComponentProps) => {
  const { user } = useAuthContext();
  const isSelf = String(user?.contact_id) === item?.metadata.contact.id;
  let message: ReactNode = (
    <>
      Wish <b>{item?.metadata.contact.displayName}</b> a happy birthday!
    </>
  );

  if (isSelf) {
    message = 'Happy birthday, cheers to a bright year ahead!';
  }

  return (
    <>
      <BadgedAvatar contact={item?.metadata.contact} badgeType="birthday" />
      <p>{message}</p>
      <DateDisplay date={date} selected={selected} />
      {selected && (
        <Actions
          noBg={noContactActions}
          contact={!noContactActions && !isSelf && item?.metadata.contact}
          activityItemID={item.id}
          noDismissal={noDismissal}
        />
      )}
    </>
  );
};

const NewMemberActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
}: ActivityListItemComponentProps) => (
  <>
    <BadgedAvatar contact={item?.metadata.contact} badgeType="new-member" />
    <p>
      <b>{item?.metadata.contact.displayName}</b> joined Clay. Give them a warm welcome!
    </p>
    <DateDisplay date={date} selected={selected} />

    {selected && (
      <Actions
        noBg={noContactActions}
        contact={!noContactActions && item?.metadata.contact}
        activityItemID={item.id}
        noDismissal={noDismissal}
      />
    )}
  </>
);

const SharedContactLink = ({ contact }: { contact: Contact }) => (
  <Link to={`/share/contact/${contact.id}`} className={styles.contactLink}>
    {contact.displayName}
  </Link>
);

const SharedGroupLink = ({ group }: { group: Group }) => (
  <Link to={`/share/group/${group.id}`} className={styles.contactLink}>
    {group.title}
  </Link>
);

const SharedActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
}: ActivityListItemComponentProps) => {
  let copy: ReactNode = '';
  if (item?.metadata.sharedContact) {
    copy = (
      <>
        {' '}
        shared the profile of <SharedContactLink contact={item?.metadata.sharedContact} /> with you.
      </>
    );
  } else if (item?.metadata.sharedGroup) {
    copy = (
      <>
        {' '}
        invited you to the group <SharedGroupLink group={item?.metadata.sharedGroup} />
      </>
    );
  }

  return (
    <>
      <BadgedAvatar contact={item?.metadata.sharedBy} badgeType="shared" />
      <p>
        <b>{item?.metadata.sharedBy.displayName}</b>
        {copy}
      </p>
      <DateDisplay date={date} selected={selected} />

      {selected && (
        <Actions
          noBg={noContactActions}
          contact={!noContactActions && item?.metadata.sharedBy}
          activityItemID={item.id}
          noDismissal={noDismissal}
        />
      )}
    </>
  );
};

const ContactListNames = ({
  contacts,
  total,
  remainderLabel = 'more people',
}: {
  contacts: Contact[];
  total: number;
  remainderLabel?: string;
}) => {
  const names = contacts.map((c, i) => (
    <ContactLink key={i} contact={c}>
      {c.firstName || c.displayName}
    </ContactLink>
  ));
  if (total === 1) {
    return names[0];
  } else if (total === 2) {
    return (
      <>
        {names[0]} and {names[1]}
      </>
    );
  } else if (total === 3) {
    return (
      <>
        {names[0]}, {names[1]}, and {names[2]}
      </>
    );
  } else {
    return (
      <>
        {names[0]}, {names[1]}, {names[2]} &amp; <b>{total - 3}</b> {remainderLabel}
      </>
    );
  }
};

const ImportDetails = ({
  importName,
  contacts,
  importTotal,
}: {
  importName: string;
  contacts: Contact[];
  importTotal: number;
}) => {
  const verb = importTotal === 1 ? 'has' : 'have';

  return (
    <>
      <ContactListNames contacts={contacts} total={importTotal} /> {verb} been added to your Clay
      via <span className={styles.source}>{importName}</span>
    </>
  );
};

const ImportActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
}: ActivityListItemComponentProps) => {
  const { importSource, importName, importTotal, contacts } = item.metadata;
  return (
    <>
      {importTotal === 1 ? (
        <>
          <BadgedAvatar contact={contacts[0]} badgeType={importSource} />
          <p>
            <ImportDetails importName={importName} contacts={contacts} importTotal={importTotal} />
          </p>
        </>
      ) : (
        <ImportAvatars contacts={contacts} importSource={importSource} importTotal={importTotal} />
      )}
      <DateDisplay date={date} selected={selected} />

      {selected && (
        <Actions
          noBg={noContactActions}
          activityItemID={item.id}
          contact={importTotal === 1 && !noContactActions && contacts[0]}
          noDismissal={noDismissal}
        />
      )}
      {importTotal > 1 && (
        <div className={styles.importDetails}>
          <ImportDetails importName={importName} contacts={contacts} importTotal={importTotal} />
        </div>
      )}
    </>
  );
};

const displayEventTimeSpan = (metadata: ActivityItemMetadata) => {
  if (metadata.startDate && metadata.endDate) {
    return ` • ${metadata.startDate.format('h:mm a')} - ${metadata.endDate.format('h:mm a')}`;
  } else if (metadata.startDate) {
    return ` • ${metadata.startDate.format('h:mm a')}`;
  }
  return '';
};

const EventActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
}: ActivityListItemComponentProps) => {
  const contacts = item?.metadata.contacts;
  const isForOneContact = contacts.length === 1;
  return (
    <>
      {isForOneContact ? (
        <BadgedAvatar contact={contacts[0]} badgeType="calendar" />
      ) : (
        <BadgedAvatarGroup contacts={contacts} badgeType="calendar" />
      )}
      <div className={cn(styles.nameAndDate, styles.eventNameAndDate, 'nameAndDate')}>
        {isForOneContact ? (
          <b className="contactName">{contacts[0].displayName}</b>
        ) : (
          <span className={styles.contactListNames}>
            <ContactListNames contacts={contacts} total={contacts.length} remainderLabel="more" />
          </span>
        )}
        <span className={cn(styles.date, 'information')}>
          {date.format('MMM d')}
          {date.isThisYear ? ' ' : date.format(' yyyy ')}
          {displayEventTimeSpan(item?.metadata)}
        </span>
      </div>
      <DateDisplay date={date} selected={selected} />
      {selected && (
        <Actions
          noBg={noContactActions}
          activityItemID={item.id}
          contact={!noContactActions && isForOneContact && contacts[0]}
          noDismissal={noDismissal}
        />
      )}
    </>
  );
};

const ReconnectDetails = ({ contacts }: { contacts: Contact[] }) => (
  <>
    Reflect on your relationship with{' '}
    <ContactListNames contacts={contacts} total={contacts.length} remainderLabel="others" />.
  </>
);

const ReconnectActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
}: ActivityListItemComponentProps) => {
  const dispatch = useDispatch();
  const { enabledNotifications } = useEnabledNotifications();
  const multiplePeoplePerReconnect = enabledNotifications['activity_group_reconnects'];
  let contacts = item.filteredContacts;

  // If for some reason there are no "active" contacts on the reconnect item, dismiss the item itself
  useEffect(() => {
    if (contacts.length === 0) {
      dispatch(dismissActivityItem(item));
    }
  }, []);

  return multiplePeoplePerReconnect ? (
    <>
      <ReconnectContactAvatarList
        className={styles.reconnectContactAvatarList}
        contacts={contacts}
        contactIsDismissed={(contactID: ID) => item.contactStatus(contactID) === 'dismissed'}
      />
      <DateDisplay date={date} selected={selected} />
      {selected && (
        <Actions
          noBg={noContactActions}
          activityItemID={item.id}
          contact={contacts.length === 1 && !noContactActions && contacts[0]}
          noDismissal={noDismissal}
          pinToTop
        />
      )}
      <div className={styles.reconnectDetails}>
        <ReconnectDetails contacts={contacts} />
      </div>
    </>
  ) : (
    <>
      <BadgedAvatar contact={contacts[0]} badgeType="reconnect" />
      <p>
        <ReconnectDetails contacts={contacts} />
      </p>
      <DateDisplay date={date} selected={selected} />
      {selected && (
        <Actions
          noBg={noContactActions}
          contact={contacts.length === 1 && !noContactActions && contacts[0]}
          activityItemID={item.id}
          noDismissal={noDismissal}
        />
      )}
    </>
  );
};

const formatDate = (date: Occasion) => {
  let day = date.format('MMM d');

  if (date.isToday) {
    day = 'today';
  } else if (date.isYesterday) {
    day = 'yesterday';
  }

  return day;
};

const TitleAndDate = ({
  title,
  what,
  where,
  date,
  noDate,
}: {
  title: string;
  what?: string;
  where?: string;
  date: Occasion;
  noDate?: boolean;
}) => {
  const day = formatDate(date);
  const dateString = [what, !noDate && day, where].filter(Boolean).join(' • ');

  return (
    <div className={cn(styles.nameAndDate, 'nameAndDate')}>
      <b className="contactName">{title}</b>
      <span className={cn(styles.date, 'information')}>{dateString}</span>
    </div>
  );
};

const NewsLink = ({ title, link, imageSrc, outlet }: ActivityItemMetadata) =>
  !!title &&
  !!link && (
    <a className={cn(styles.newsLink, 'newsLink')} href={link} target="_blank">
      <div className={styles.copy}>
        <div className={styles.title}>{title}</div>
        <div className={cn(styles.source, 'information-item')}>via {outlet}</div>
      </div>
      {imageSrc && <div className={styles.image} style={{ backgroundImage: `url(${imageSrc})` }} />}
    </a>
  );

const NewsActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
  onlyRelativeDate,
}: ActivityListItemComponentProps) => {
  const { contact, imageSrc, link, title, outlet } = item.metadata;
  return (
    <>
      <BadgedAvatar contact={contact} badgeType="news" />
      <TitleAndDate
        title={contact.displayName}
        what="In the news"
        date={date}
        noDate={onlyRelativeDate}
      />
      <DateDisplay date={date} selected={selected} />
      {selected && (
        <Actions
          activityItemID={item.id}
          contact={contact}
          pinToTop
          noBg={noContactActions}
          noDismissal={noDismissal}
        />
      )}
      <NewsLink title={title} link={link} imageSrc={imageSrc} outlet={outlet} />
    </>
  );
};

const ReminderActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
}: ActivityListItemComponentProps) => {
  const paddedDate = useMemo(() => new Occasion(date.addDays(1)), []);
  const day = formatDate(date);

  return (
    <>
      <BadgedAvatar contact={item?.metadata.contact} badgeType="reminder" date={paddedDate} />
      <div className={styles.reminderDetails}>
        You set a reminder for{' '}
        <ContactLink contact={item.contact}>{item.contact.displayName}</ContactLink> {day} at{' '}
        {date.format('h:mm a')}
      </div>
      <DateDisplay date={date} selected={selected} />
      {selected && (
        <Actions
          noBg={noContactActions}
          activityItemID={item.id}
          contact={!noContactActions && item?.metadata.contact}
          noDismissal={noDismissal}
          pinToTop
        />
      )}
    </>
  );
};

const ExpandableContent = ({
  renderedContent,
  activityItemID,
}: {
  renderedContent: ReactNode;
  activityItemID: ID;
}) => {
  const dispatch = useDispatch();

  return (
    <div className={styles.copyContainer}>
      <div className={cn(styles.copy, styles.firstLine)}>{renderedContent}</div>
      <div className={cn(styles.copy, styles.secondLine)}>{renderedContent}</div>
      <SmallButton
        className={cn(styles.seeMoreBtn, 'seeMoreBtn')}
        onClick={(e) => {
          dispatch(openActivityItemInModal(activityItemID));
          e.stopPropagation();
        }}
      >
        Expand
      </SmallButton>
    </div>
  );
};

const DiffActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
  onlyRelativeDate,
}: ActivityListItemComponentProps) => {
  const { contact, content, subtype, source } = item?.metadata || {};
  const expandableDiff = subtype === 'bio';
  const changeType = source === 'linkedin' ? 'headline' : subtype;

  return (
    <>
      <BadgedAvatar contact={contact} badgeType={source} />
      <TitleAndDate
        title={contact.displayName}
        where={`via ${source}`}
        what={`${changeType} Change`}
        date={date}
        noDate={onlyRelativeDate}
      />
      <DateDisplay date={date} selected={selected} />
      {selected && (
        <Actions
          noBg={noContactActions}
          activityItemID={item.id}
          contact={!noContactActions && item?.metadata.contact}
          noDismissal={noDismissal}
          pinToTop
        />
      )}
      {!expandableDiff && <div className={styles.diffContent}>{renderDiffMarkdown(content)}</div>}
      {expandableDiff && (
        <div className={styles.postContent}>
          <ExpandableContent
            renderedContent={renderDiffMarkdown(content)}
            activityItemID={item.id}
          />
        </div>
      )}
    </>
  );
};

const LinkedInAnnouncement = ({ item }: { item: ActivityItem }) => {
  const announcement = item.metadata.linkedInAnnouncement;
  const { company, companyHref, years, jobTitle } = announcement.metadata;
  const $yay = <b>🎉</b>;
  const $name = <b>{item?.metadata.contact.displayName}</b>;

  let $company: ReactNode = company || 'their company';
  if (company && companyHref) {
    $company = (
      <a href={companyHref} target="_blank" onClick={(e) => e.stopPropagation()}>
        {company}
      </a>
    );
  }

  const $duration = years ? `${years} ${pluralize(years, 'year')}` : 'a work anniversary';

  if (announcement.type === 'anniversary') {
    return (
      <>
        {$name} is celebrating {$duration} at {$company} {$yay}
      </>
    );
  }

  if (announcement.type === 'new-job') {
    return (
      <>
        {$name} started a new job{jobTitle ? ` as ${jobTitle}` : ''} at {$company} {$yay}
      </>
    );
  }

  if (announcement.type === 'promotion') {
    return (
      <>
        {$name} was promoted{jobTitle ? ` to ${jobTitle}` : ''} at {$company} {$yay}
      </>
    );
  }

  return <>{renderPostMarkdown(item.metadata.content, true)}</>;
};

const PostActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
  onlyRelativeDate,
}: ActivityListItemComponentProps) => {
  const expandablePost = item?.metadata.postSubtype !== 'announcement';
  const { contact, source, content, media } = item?.metadata || {};

  return (
    <>
      <BadgedAvatar contact={contact} badgeType={source} />
      {expandablePost ? (
        <TitleAndDate
          title={contact.displayName}
          where={`via ${source}`}
          date={date}
          noDate={onlyRelativeDate}
        />
      ) : (
        <div className={styles.postAnnouncement}>
          <LinkedInAnnouncement item={item} />
        </div>
      )}
      <DateDisplay date={date} selected={selected} />
      {selected && (
        <Actions
          noBg={noContactActions}
          activityItemID={item.id}
          contact={!noContactActions && contact}
          noDismissal={noDismissal}
          pinToTop
        />
      )}
      {expandablePost && (
        <div className={styles.postContent}>
          <ExpandableContent
            renderedContent={renderPostMarkdown(content, true)}
            activityItemID={item.id}
          />
          {media?.length > 0 && (
            <div className={styles.imageStack}>
              {media.slice(0, 3).map(({ thumbnail }) => (
                <div
                  key={thumbnail}
                  className={styles.previewImage}
                  style={{ backgroundImage: `url(${thumbnail})` }}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

const MemorialActivityItem = ({ item, date, selected }: ActivityListItemComponentProps) => {
  const { contact, memorial } = item?.metadata || {};
  let dateDisplay = memorial?.format('MMMM do') || 'Today';

  if (memorial?.isToday) {
    dateDisplay = 'Today';
  } else if (memorial?.isYesterday) {
    dateDisplay = 'Yesterday';
  }

  return (
    <>
      <BadgedAvatar contact={contact} badgeType="memorial" />
      <p>
        {dateDisplay} {dateDisplay === 'Today' ? 'marks' : 'marked'} the anniversary of the passing
        of <b>{contact.displayName}</b>.
      </p>
      <DateDisplay date={date} selected={selected} />
      {selected && <Actions activityItemID={item.id} />}
    </>
  );
};

const subtypeToTitle: Record<ActivityAnnouncementSubtype, string> = {
  'intro-video': 'Learning the basics',
  'welcome-message': 'Welcome to Clay',
  'booklet': 'Personalized welcome guide',
  'search-suggestions': 'Discover your relationships',
  'connect-integrations': 'Connect all your accounts',
};

const AnnouncementContent = ({ item }: { item: ActivityItem }) => {
  return (
    <div className={styles.announcementDetails}>
      {renderPostMarkdown(item?.metadata.content)}
      {item?.metadata.subtype === 'connect-integrations' && <ConnectIntegrations />}
      {item?.metadata.subtype === 'search-suggestions' && (
        <SearchSuggestions terms={item.metadata.terms} />
      )}
    </div>
  );
};

const AnnouncementActivityItem = ({
  item,
  date,
  selected,
  noDismissal,
}: ActivityListItemComponentProps) => {
  const { user } = useAuthContext();
  const { subtype } = item?.metadata;
  let title = subtypeToTitle[subtype];

  if (subtype === 'welcome-message') {
    title = `${title}, ${user?.first_name}!`;
  }

  return (
    <>
      <ClayBadge />
      <TitleAndDate title={title} what="Welcome to Clay" date={date} />
      <DateDisplay date={date} selected={selected} />
      {selected && <Actions activityItemID={item.id} noDismissal={noDismissal} pinToTop noBg />}
      <AnnouncementContent item={item} />
    </>
  );
};

interface ActivityListItemProps {
  item?: ActivityItem;
  selected: boolean;
  noContactActions?: boolean;
  navigate?: (path: string) => void;
  noDismissal?: boolean;
  onlyRelativeDate?: boolean;
}

export default function ActivityListItem({
  item,
  selected,
  noContactActions,
  noDismissal,
  onlyRelativeDate,
}: ActivityListItemProps) {
  const props: ActivityListItemComponentProps = {
    item,
    date: item.date,
    selected,
    noContactActions: !!noContactActions,
    noDismissal: !!noDismissal,
    onlyRelativeDate: !!onlyRelativeDate,
  };

  switch (item.type) {
    case 'birthday':
      return <BirthdayActivityItem {...props} />;

    case 'memorial':
      return <MemorialActivityItem {...props} />;

    case 'new-member':
      return <NewMemberActivityItem {...props} />;

    case 'shared':
      return <SharedActivityItem {...props} />;

    case 'import':
      return <ImportActivityItem {...props} />;

    case 'reconnect':
      return <ReconnectActivityItem {...props} />;

    case 'reminder':
      return <ReminderActivityItem {...props} />;

    case 'news':
      return <NewsActivityItem {...props} />;

    case 'event':
      return <EventActivityItem {...props} />;

    case 'diff':
      return <DiffActivityItem {...props} />;

    case 'post':
      return <PostActivityItem {...props} />;

    case 'announcement':
      return <AnnouncementActivityItem {...props} />;

    default:
      return <div>TODO: {item.type}</div>;
  }
}
